import { z } from 'zod';
import {
  grupoSchema,
  isoDateSchema,
  isoDateTimeSchema,
  optional,
  situacaoPlanoSchema,
  situacaoProgSchema,
  statusPedidoSchema,
  versionSchema,
} from './common';
import { linhaPedidoSchema } from './orders';

export const resumoPlanoSchema = z.object({
  pedidos: z.number().int().nonnegative(),
  'no-prazo': z.number().int().nonnegative(),
  'em-risco': z.number().int().nonnegative(),
  atrasados: z.number().int().nonnegative(),
  'nao-programados': z.number().int().nonnegative(),
  'total-kg': z.number().nonnegative(),
  'maquina-gargalo': z.string(),
  'pct-carga-gargalo': z.number(),
});
export type ResumoPlano = z.infer<typeof resumoPlanoSchema>;

export const planoSchema = z.object({
  'nr-plano': z.number().int(),
  descricao: z.string(),
  'dt-ini': isoDateSchema,
  'dt-fim': isoDateSchema,
  situacao: situacaoPlanoSchema,
  'nr-plano-origem': optional(z.number().int()),
  'dt-hr-publicacao': optional(isoDateTimeSchema),
  'usuario-publicacao': optional(z.string()),
  resumo: resumoPlanoSchema,
  version: versionSchema,
});
export type Plano = z.infer<typeof planoSchema>;

export const fixadoSchema = z.object({ maquina: z.boolean(), data: z.boolean(), sequencia: z.boolean() });
export type Fixado = z.infer<typeof fixadoSchema>;

export const etapaProgramadaSchema = z.object({
  'id-prog': z.number().int(),
  'nr-plano': z.number().int(),
  'nome-abrev': z.string(),
  'nr-pedcli': z.string(),
  'nr-sequencia': z.number().int(),
  'nr-ord-produ': optional(z.number().int()),
  'it-codigo': z.string(),
  'descricao-item': optional(z.string()),
  maquina: z.string(),
  'dt-prog': isoDateSchema,
  sequencia: z.number().int().positive(),
  espessura: z.number(),
  largura: z.number(),
  'peso-especifico': z.number(),
  'qt-planejada': z.number(),
  velocidade: z.number(),
  'taxa-kgh': z.number(),
  'horas-producao': z.number(),
  'horas-setup': z.number(),
  'motivos-setup': optional(z.array(z.string())), // ["COR Âmbar→Cristal 180 min"]
  'dt-hr-inicio': isoDateTimeSchema,
  'dt-hr-fim': isoDateTimeSchema,
  'dt-liberacao': optional(isoDateSchema), // fim da cura
  'dt-entrega': isoDateSchema,
  'largura-mae': optional(z.number()),
  'qtd-pistas': optional(z.number().int()),
  'pct-aproveit': optional(z.number()),
  'id-combinacao': optional(z.number().int()),
  fixado: fixadoSchema,
  congelado: z.boolean(),
  'status-pedido': statusPedidoSchema,
  situacao: situacaoProgSchema,
  version: versionSchema,
});
export type EtapaProgramada = z.infer<typeof etapaProgramadaSchema>;

export const celulaCargaSchema = z.object({
  maquina: z.string(),
  data: isoDateSchema,
  'horas-usadas': z.number(),
  'horas-disponiveis': z.number(),
  'horas-parada': z.number(),
  'pct-carga': z.number(),
  etapas: z.number().int().nonnegative(),
});
export type CelulaCarga = z.infer<typeof celulaCargaSchema>;

export const moverEtapaSchema = z.object({
  maquina: z.string().optional(),
  'dt-prog': isoDateSchema.optional(),
  sequencia: z.number().int().positive().optional(),
  fixar: fixadoSchema.partial().optional(),
  'confirma-congelado': z.boolean().optional(),
  version: versionSchema,
});
export type MoverEtapa = z.infer<typeof moverEtapaSchema>;

export const resultadoMovimentacaoSchema = z.object({
  etapa: etapaProgramadaSchema,
  afetadas: z.array(etapaProgramadaSchema),
  plano: planoSchema,
  avisos: z.array(z.string()),
});
export type ResultadoMovimentacao = z.infer<typeof resultadoMovimentacaoSchema>;

export const planoNovoSchema = z.object({
  descricao: z.string().trim().min(1).max(120),
  'dt-ini': isoDateSchema,
  'dias-horizonte': z.number().int().min(1).max(90),
  'nr-plano-origem': z.number().int().optional(),
});
export type PlanoNovo = z.infer<typeof planoNovoSchema>;

export const prioridadesSchema = z.object({
  'ids-pedido': z.array(z.string()).min(1),
  version: versionSchema,
});
export type Prioridades = z.infer<typeof prioridadesSchema>;

export const copiarPlanoSchema = z.object({ descricao: z.string().trim().min(1).max(120) });
export type CopiarPlano = z.infer<typeof copiarPlanoSchema>;

export const pedidoDoPlanoSchema = linhaPedidoSchema.extend({
  'status-pedido': statusPedidoSchema,
  'dt-fim-prevista': optional(isoDateTimeSchema),
  'folga-horas': optional(z.number()),
  prioridade: z.number().int().positive(),
});
export type PedidoDoPlano = z.infer<typeof pedidoDoPlanoSchema>;

export const demandaMaterialSchema = z.object({
  'it-codigo': z.string(),
  descricao: z.string(),
  unidade: z.literal('kg'),
  semanas: z.array(
    z.object({ 'semana-iso': z.number().int(), 'dt-inicio': isoDateSchema, 'demanda-kg': z.number() }),
  ),
  'total-kg': z.number(),
  'estoque-kg': z.number(),
  'em-transito': z.array(z.object({ 'quantidade-kg': z.number(), 'dt-prevista': isoDateSchema })),
  cobertura: z.enum(['coberto', 'risco', 'critico']),
  'primeira-semana-falta': optional(z.number().int()),
});
export type DemandaMaterial = z.infer<typeof demandaMaterialSchema>;

export const celulaAderenciaSchema = z.object({
  maquina: z.string(),
  data: isoDateSchema,
  'kg-planejado': z.number(),
  'kg-produzido': z.number(),
  'pct-aderencia': z.number(),
});
export type CelulaAderencia = z.infer<typeof celulaAderenciaSchema>;

// ---------- Queries ----------

export const planosQuerySchema = z.object({
  situacao: z.coerce.number().pipe(situacaoPlanoSchema).optional(),
});
export type PlanosQuery = z.infer<typeof planosQuerySchema>;

export const deAteQuerySchema = z.object({ de: isoDateSchema.optional(), ate: isoDateSchema.optional() });
export type DeAteQuery = z.infer<typeof deAteQuerySchema>;

export const etapasQuerySchema = z.object({
  data: isoDateSchema.optional(),
  maquina: z.string().optional(),
  'nome-abrev': z.string().optional(),
});
export type EtapasQuery = z.infer<typeof etapasQuerySchema>;

export { grupoSchema };
